import React, { useEffect, useMemo, useState } from "react";
import "../styles/AppLogo.css";
import SettingServices from "../services/SettingServices";

/**
 * Logo like image 1: circular icon with "Y", "Yantra" bold, "GAMING" subtext.
 * Use in Sidebar and Outlet header.
 */
const BRAND_CACHE_KEY = "YANTRA_APP_BRAND_V1";
let inMemoryBrand = null; // { main_title, sub_title }
let inFlight = null;

const readCachedBrand = () => {
  if (inMemoryBrand) return inMemoryBrand;
  try {
    const raw = sessionStorage.getItem(BRAND_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const main_title = String(parsed.main_title || "").trim();
    const sub_title = String(parsed.sub_title || "").trim();
    if (!main_title && !sub_title) return null;
    inMemoryBrand = { main_title, sub_title };
    return inMemoryBrand;
  } catch (_) {
    return null;
  }
};

const writeCachedBrand = (brand) => {
  try {
    inMemoryBrand = brand || null;
    if (!brand) {
      sessionStorage.removeItem(BRAND_CACHE_KEY);
      return;
    }
    sessionStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(brand));
  } catch (_) {
    // ignore
  }
};

const fetchBrandOnce = () => {
  if (inMemoryBrand) return Promise.resolve(inMemoryBrand);
  if (inFlight) return inFlight;
  const api = new SettingServices();
  inFlight = Promise.resolve(api.getBrand())
    .then((res) => {
      if (!res?.isSuccess) return null;
      const main_title = String(res?.data?.main_title || "").trim();
      const sub_title = String(res?.data?.sub_title || "").trim();
      const brand = { main_title, sub_title };
      if (!brand.main_title && !brand.sub_title) return null;
      writeCachedBrand(brand);
      return brand;
    })
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

const AppLogo = ({
  className = "",
  showSubtext = true,
  size = "medium",
  theme = "dark", // dark | light
  title,
  subTitle,
}) => {
  const initial = useMemo(
    () =>
      readCachedBrand() || {
        main_title: "Yantra",
        sub_title: "GAMING",
      },
    [],
  );
  const [brand, setBrand] = useState(initial);

  useEffect(() => {
    // Background refresh
    fetchBrandOnce().then((b) => {
      if (b) setBrand(b);
    });

    const onBrandUpdated = (e) => {
      const next = e?.detail;
      const main_title = String(next?.main_title || "").trim();
      const sub_title = String(next?.sub_title || "").trim();
      const b = { main_title, sub_title };
      writeCachedBrand(b);
      setBrand(b);
    };
    window.addEventListener("app-brand-updated", onBrandUpdated);
    return () => window.removeEventListener("app-brand-updated", onBrandUpdated);
  }, []);

  const finalTitle =
    title != null && String(title).trim() !== "" ? String(title).trim() : brand.main_title;
  const finalSubTitle =
    subTitle != null && String(subTitle).trim() !== ""
      ? String(subTitle).trim()
      : brand.sub_title;

  return (
    <div className={`app-logo ${size} theme-${theme} ${className}`.trim()}>
      <div className="app-logo-circle">
        <span aria-hidden="true">{finalTitle.charAt(0)}</span>
      </div>
      <div className="app-logo-text">
        <h3 className="app-logo-title">{finalTitle || "Yantra"}</h3>
        {showSubtext && (
          <p className="app-logo-sub">{finalSubTitle || "GAMING"}</p>
        )}
      </div>
    </div>
  );
};

export default AppLogo;
